import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from '@kognitos/lattice';
import {
  useRenameAutomation,
  getGetAutomationQueryKey,
  getListAutomationsQueryKey,
  getListAutomationsInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
} from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { errorHandler } from '@/shared/utils/error-handler';

/**
 * Renames an automation and invalidates every list/detail query that shows its
 * display name. Shared by the page header title and the document title bar so
 * a rename from either place refreshes the same set of caches.
 *
 * `rename` rethrows on failure, which is what `EditableTitle` needs to revert
 * the field instead of leaving stale text on screen.
 */
export function useRenameAutomationTitle(automationId?: string) {
  const { t: tAutomation } = useTranslation('automation');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const queryClient = useQueryClient();

  const { mutateAsync: renameAutomation, isPending: isRenaming } =
    useRenameAutomation({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
        },
        onError: error => {
          errorHandler(error, () => {
            toast.error(tAutomation('automation.rename.error'));
          });
        },
      },
    });

  const rename = async (nextTitle: string) => {
    if (!automationId) return;
    await renameAutomation({
      organization: currentOrganizationId,
      workspace: currentWorkspaceId,
      automation: automationId,
      data: { display_name: nextTitle },
    });
  };

  return { rename, isRenaming };
}
